import { Component, OnInit, Input, ViewChild, AfterViewInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportProposalViewModel } from '../../index';
import { ReportService } from '../../api/report.service';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, Validators } from '@angular/forms';
import { ReportHelperService } from '../../../services/report-helper.service';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-report-proposal-list',
    templateUrl: './report-proposal-list.component.html',
    styleUrls: ['./report-proposal-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportProposalListComponent implements OnInit {
    reportAspectGroupId: number;
    reportAspectGroupName: string;
    reportId: number;
    hasPermissionToChange$: Observable<boolean>;

    proposalForm: UntypedFormGroup;
    selectedIndex?: number = null;

    loadingIndicator: boolean;
    isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<ReportProposalListComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        private reportHelperService: ReportHelperService) {

        this.reportAspectGroupId = data.reportAspectGroupId;
        this.reportAspectGroupName = data.reportAspectGroupName;
        this.reportId = data.reportId;
        this.hasPermissionToChange$ = data.hasPermissionToChange$;
    }

    ngOnInit() {
        this.buildForm();
        this.loadData();

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new EventMapper(r, this))).subscribe((e) => {
                if (e.event.key === "Escape") {
                    e.component.cancel();
                }
            }
        );
    }

    buildForm() {
        this.proposalForm = this.formBuilder.group({
            proposals: this.formBuilder.array([this.buildChildArray(<ReportProposalViewModel>{})])
        });
    }


    buildChildArray(proposal : ReportProposalViewModel): UntypedFormGroup {
        return this.formBuilder.group({
            comment: proposal.comment || '',
            details: [proposal.details || '', Validators.required],
            fromPreviousReport: proposal.fromPreviousReport || false,
            fixed: proposal.fixed || false,
            notApplicable: proposal.notApplicable || false,
            reportProposalId:proposal.reportProposalId || '',
            reportAspectGroupId: proposal.reportAspectGroupId || '',
            reportId: proposal.reportId || '',
            //sortOrder: proposal.sortOrder || '',
            originalDate: proposal.originalDate || ''
    });
    }

    resetForm(proposals: ReportProposalViewModel[]) {
        this.proposalForm.reset();
        let proposalFormGroups = proposals.map(p => this.formBuilder.group(p));
        let proposalFormArray = this.formBuilder.array(proposalFormGroups);
        this.proposalForm.setControl('proposals', proposalFormArray);
    }

    get proposalsArray(): UntypedFormArray {
        return this.proposalForm.get('proposals') as UntypedFormArray;
    }

    public getProposalTitle(index: number, proposalRecord: any): string {
        let value: string = proposalRecord.get("details").value;

        let result: string = (index + 1).toString() + ". ";

        if (value != null) {
            if (value.length > 117) {
                result += value.substring(0, 117) + "...";
            } else {
                result += value;
            }
        }

        return result;
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.reportService.getProposalsByReportAspectGroupId(this.reportAspectGroupId).subscribe(
            reqs => this.onDataLoadSuccessful(reqs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(proposals: ReportProposalViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(proposals);
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }

    public save(close: boolean = true) {
        if (!this.proposalForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        let container = this.proposalForm.value;
        let proposals: ReportProposalViewModel[] = container.proposals;

        this.reportService.updateReportProposals(this.reportAspectGroupId, proposals).subscribe(
            t => this.saveCompleted(close, t),
            error => this.saveFailed(error));

    }

    private saveCompleted(close: boolean, proposals: ReportProposalViewModel[]) {
        this.isSaving = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"),
            "Förslag " + this.translationService.getTranslation("globalTexts.saved"),
            MessageSeverity.success);

        if (close) {
            this.dialogRef.close(true);
        } else {
            this.resetForm(proposals);
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"),
            this.translationService.getTranslation("globalTexts.SaveErrorMessage"),
            MessageSeverity.error,
            error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }


    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.proposalForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 })
                .onAction().subscribe(() => {
                    this.dialogRef.close(this.anySaveOperationsPerformed);
                }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

    public add(panel: MatExpansionPanel, event: MouseEvent, index: number): void {
        event.stopPropagation();

        // Close panel since an error message is displayed when setting the selectedIndex later
        if (panel != null && panel.expanded) {
            panel.close();
        }

        let proposal: ReportProposalViewModel = <ReportProposalViewModel>{
            fromPreviousReport: false,
            fixed: false,
            notApplicable: false,
            reportAspectGroupId: this.reportAspectGroupId,
            reportId: this.reportId
        };

        var childForm = this.buildChildArray(proposal);

        this.proposalsArray.insert(index + 1, childForm);
        this.selectedIndex = index + 1;
    }

    public remove(event: MouseEvent, index: number): void {
        event.stopPropagation();

        this.proposalsArray.removeAt(index);
        this.selectedIndex = null;
    }

    public moveUp(event: MouseEvent, index: number): void {
        event.stopPropagation();
        if (index > 0) {
            var formToMove = this.proposalsArray.at(index);
            this.proposalsArray.removeAt(index);
            this.proposalsArray.insert(index - 1, formToMove);
        }
    }

    public moveDown(event: MouseEvent, index: number): void {
        event.stopPropagation();
        if (index < this.proposalsArray.length - 1) {
            var formToMove = this.proposalsArray.at(index);
            this.proposalsArray.removeAt(index);
            this.proposalsArray.insert(index + 1, formToMove);
        }
    }

    public displayError(error: any): string {
        return error;
    }
}

export class EventMapper {
    component: ReportProposalListComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: ReportProposalListComponent) {
        this.component = component;
        this.event = event;
    }
}

